// React から useMemo フックをインポート
import { useMemo } from 'react';
// react-hook-form から UseFormReturn 型をインポート
import { UseFormReturn } from 'react-hook-form';
// 一般アンケートの型定義をインポート
import { GENERAL_ENQUETE_JSON_INPUT_TYPE, Question, QuestionState } from 'src/@types/generalEnquete';
// Redux ストアセレクターをインポート
// アンケート回答検証フックをインポート
import { useEnqueteAnswerValidator } from '../../type-general-question/components/tabs/question-and-answer/hooks/useEnqueteAnswerValidator';
// 共通フォームコンポーネントをインポート
import {
  EnqueteAnswerCheckboxForm,
  EnqueteAnswerCommentForm,
  EnqueteAnswerDateForm,
  EnqueteAnswerDropdownForm,
  EnqueteAnswerForm,
  EnqueteAnswerPeriodForm,
  EnqueteAnswerRadioGroupForm,
  EnqueteAnswerTextForm,
} from './common/shared';

// フォームスイッチャーコンポーネントの props 型を定義
type EnqueteAnswerSpecialQuestionCompliaFormSwitcher = {
  // フォームメソッドを定義
  methods: UseFormReturn;
  // 質問オブジェクトを定義
  question: Question;
  // インデックスを定義
  index: number;
  // 言語コードを定義（オプション）
  languageCode?: string;
  // 質問状態の配列を定義（オプション）
  questionStates?: QuestionState[];
};

// EnqueteAnswerSpecialQuestionCompliaFormSwitcher コンポーネントを定義
const EnqueteAnswerSpecialQuestionCompliaFormSwitcher = ({
  // フォームメソッドを props から取得
  methods,
  // 質問データを props から取得
  question,
  // インデックスを props から取得
  index,
  // 言語コードを props から取得
  languageCode,
  // 質問状態を props から取得
  questionStates,
}: EnqueteAnswerSpecialQuestionCompliaFormSwitcher) => {
  // 検証フックを使用して検証ルールを取得
  const { rulesEnqueteAnswer, rulesEnqueteAnswerCheckbox } = useEnqueteAnswerValidator(methods as UseFormReturn, question);

  // 検証ルールをメモ化して性能を最適化
  const memolizedRule = useMemo(() => rulesEnqueteAnswer, [rulesEnqueteAnswer]);
  // チェックボックス用の検証ルールをメモ化して性能を最適化
  const memolizedRuleCheckBox = useMemo(() => rulesEnqueteAnswerCheckbox, [rulesEnqueteAnswerCheckbox]);
  // 入力タイプに応じてコンポーネントを切り替える関数を定義
  const renderSwitch = (type: string) => {
    // 入力タイプに基づいてスイッチ処理を開始
    switch (type) {
      // 入力タイプがテキストの場合
      case GENERAL_ENQUETE_JSON_INPUT_TYPE.TEXT:
        // テキストフォームコンポーネントを返す
        return (
          <EnqueteAnswerTextForm
            // 質問データを渡す
            question={question}
            // インデックスを渡す
            index={index}
            // フォームメソッドを渡す
            methods={methods}
            // 検証ルールを渡す
            validationRule={rulesEnqueteAnswer}
            // 言語コードを渡す
            languageCode={languageCode}
            // 質問状態を渡す
            questionState={questionStates?.find((questionState) => questionState.questionId === question.id)?.state}
          />
        );
      // 入力タイプがテキストエリアの場合
      case GENERAL_ENQUETE_JSON_INPUT_TYPE.TEXTAREA:
        // コメントフォームコンポーネントを返す
        return (
          <EnqueteAnswerCommentForm
            // 質問データを渡す
            question={question}
            // インデックスを渡す
            index={index}
            // フォームメソッドを渡す
            methods={methods}
            // メモ化された検証ルールを渡す
            validationRule={memolizedRule}
            // 言語コードを渡す
            languageCode={languageCode}
            // 質問状態を渡す
            questionState={questionStates?.find((questionState) => questionState.questionId === question.id)?.state}
          />
        );
      // 入力タイプがチェックボックスの場合
      case GENERAL_ENQUETE_JSON_INPUT_TYPE.CHECKBOX:
        // チェックボックスフォームコンポーネントを返す
        return (
          <EnqueteAnswerCheckboxForm
            // 質問データを渡す
            question={question}
            // インデックスを渡す
            index={index}
            // フォームメソッドを渡す
            methods={methods}
            // メモ化されたチェックボックス用検証ルールを渡す
            validationRule={memolizedRuleCheckBox}
            // 質問状態を渡す
            questionState={questionStates?.find((questionState) => questionState.questionId === question.id)?.state}
          />
        );
      // 入力タイプがラジオグループの場合
      case GENERAL_ENQUETE_JSON_INPUT_TYPE.RADIOGROUP:
        // ラジオグループフォームコンポーネントを返す
        return (
          <EnqueteAnswerRadioGroupForm
            // 質問データを渡す
            question={question}
            // インデックスを渡す
            index={index}
            // フォームメソッドを渡す
            methods={methods}
            // メモ化された検証ルールを渡す
            validationRule={memolizedRule}
            // 質問状態を渡す
            questionState={questionStates?.find((questionState) => questionState.questionId === question.id)?.state}
          />
        );
      // 入力タイプがセレクトボックスの場合
      case GENERAL_ENQUETE_JSON_INPUT_TYPE.SELECT:
        // ドロップダウンフォームコンポーネントを返す
        return (
          <EnqueteAnswerDropdownForm
            // 質問データを渡す
            question={question}
            // インデックスを渡す
            index={index}
            // フォームメソッドを渡す
            methods={methods}
            // メモ化された検証ルールを渡す
            validationRule={memolizedRule}
            // 質問状態を渡す
            questionState={questionStates?.find((questionState) => questionState.questionId === question.id)?.state}
          />
        );
      // 入力タイプが日付の場合
      case GENERAL_ENQUETE_JSON_INPUT_TYPE.DATE:
        // 日付フォームコンポーネントを返す
        return (
          <EnqueteAnswerDateForm
            // 質問データを渡す
            question={question}
            // インデックスを渡す
            index={index}
            // フォームメソッドを渡す
            methods={methods}
            // 言語コードを渡す
            languageCode={languageCode}
            // 質問状態を渡す
            questionState={questionStates?.find((questionState) => questionState.questionId === question.id)?.state}
          />
        );
      // 入力タイプが日付範囲の場合
      case GENERAL_ENQUETE_JSON_INPUT_TYPE.DATERANGE:
        // 日付範囲フォームコンポーネントを返却する
        return (
          <EnqueteAnswerPeriodForm
            // 質問データを渡す
            question={question}
            // インデックスを渡す
            index={index}
            // フォームメソッドを渡す
            methods={methods}
            // 言語コードを渡す
            languageCode={languageCode}
            // 質問状態を渡す
            questionState={questionStates?.find((questionState) => questionState.questionId === question.id)?.state}
          />
        );
      // デフォルトの場合
      default:
        // null を返す
        return null;
    }
  };

  // 入力タイプが存在するか判定してレンダリング
  return question.inputType ? (
    // 質問フォームラッパーコンポーネントをレンダリング
    <EnqueteAnswerForm question={question} index={index} languageCode={languageCode} questionStates={questionStates}>
      {/* 入力タイプに応じたコンポーネントをレンダリング */}
      {renderSwitch(question.inputType)}
    </EnqueteAnswerForm>
  ) : null;
};

// EnqueteAnswerSpecialQuestionCompliaFormSwitcher コンポーネントをデフォルトエクスポート
export default EnqueteAnswerSpecialQuestionCompliaFormSwitcher;
